import { html, LitElement } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles.ts';
import { ButtonEnums } from '../button/index.ts';
import { AtlChatInputProps } from './messaging.props.ts';
import ChatInputStyles from './chat-input.scss?inline';

import { classMap } from 'lit/directives/class-map.js';
import { ChatInputUpdatedEvent, ChatSentEvent } from './messaging.events.ts';
import * as TagNames from './messaging.tagnames.ts';
import { ifDefined } from 'lit/directives/if-defined.js';

@customElement(TagNames.ChatInput)
export class AtlChatInput extends LitElement implements AtlChatInputProps {
   declare chatInputBoxPlaceholder?: string;
   declare disableChatSend?: boolean;
   declare sendButtonTooltip?: string;

   declare _hasContent?: boolean;

   static properties = {
      chatInputBoxPlaceholder: { type: String },
      disableChatSend: { attribute: 'disable-chat-send', type: Boolean },
      _hasContent: { type: Boolean, state: true },
      sendButtonTooltip: { attribute: 'send-button-tooltip', type: String },
   };

   constructor() {
      super();
      this._hasContent = false;
      this.addEventListener(ChatInputUpdatedEvent.EVENT_NAME, (e: Event) => {
         const customEvent = e as ChatInputUpdatedEvent;
         if (customEvent.detail) {
            this.chatInputBoxValue = customEvent.detail;
         }
      });
   }

   static styles = [...getGlobalStyleSheets(), ChatInputStyles];

   @query('.chat-input-box')
   private inputElement!: HTMLDivElement;

   private get chatInputBoxValue(): string | undefined {
      return this.inputElement?.textContent ?? undefined;
   }

   private set chatInputBoxValue(value: string) {
      this.inputElement.textContent = value;
      const trimmedValue = value.trim();
      this._hasContent = trimmedValue.length > 0;
   }


   private _handleInput = (event: InputEvent) => {
      const textContent = (event.target as HTMLDivElement).textContent ?? '';
      this._hasContent = textContent.trim().length > 0;
   };

   private _handlePaste = () => {
      queueMicrotask(() => {
         const textContent = this.inputElement?.textContent ?? '';
         this._hasContent = textContent.trim().length > 0;
      });
   };

   handleKeyPress(event: KeyboardEvent) {
      if (this.disableChatSend) return;

      if (event.code.toLowerCase() === 'enter' && !event.shiftKey) {
         event.preventDefault();
         this._onSentClick();
      }
   }

   private _onSentClick = () => {
      if (this.disableChatSend) return;
      if (!this.chatInputBoxValue?.trim()?.length) return;

      const message = this.chatInputBoxValue.trim();
      // Clear before dispatching so that any synchronous handler (e.g. .NET WASM interop)
      // that writes back to the input during the event dispatch is not overwritten by this clear.
      this.chatInputBoxValue = '';
      this.dispatchEvent(new ChatSentEvent(message));
   };

   render() {
      return html`
         <div class="messaging-chat-panel-footer">
            <div class="messaging-chat-panel-footer-body">
            <slot name="context"></slot>
               <div class="d-flex">
                  <div
                     part="chat-text-input"
                     class=${classMap({
                        'chat-input-box': true,
                        'has-content': this._hasContent === true,
                     })}
                     contenteditable="plaintext-only"
                     data-placeholder=${this.chatInputBoxPlaceholder ??
                     'Type your message'}
                     aria-multiline="true"
                     aria-label="Chat message input box"
                     tabindex="0"
                     role="textbox"
                     @input=${this._handleInput}
                     @paste=${this._handlePaste}
                     @keypress=${this.handleKeyPress}
                  ></div>
               </div>
               <div class="d-flex align-content-center footer-buttons">
                  <slot name="actions"></slot>
                  <div class="col d-flex justify-content-end">
                     <div
                        class=${classMap({
                           'send-button-wrapper': true,
                           visible: this._hasContent === true,
                        })}
                        ?inert=${!this._hasContent}
                        aria-hidden=${!this._hasContent ? 'true' : 'false'}
                     >
                        <atlantis-button
                           variant=${ButtonEnums.ButtonVariant.Action}
                           button-classes="text-secondary"
                           iconName="paper-plane"
                           ?disabled=${this.disableChatSend}
                           .buttonTooltip=${ifDefined(this.sendButtonTooltip)}
                           data-bs-toggle=${ifDefined(
                              this.sendButtonTooltip ? 'tooltip' : undefined
                           )}
                           @click=${this._onSentClick}
                           .buttonAriaSettings=${{
                              ariaLabel: 'Send message',
                           }}
                        >
                        </atlantis-button>
                     </div>
                  </div>
               </div>
            </div>
            <div class="mt-2 messaging-chat-panel-footer-extended">
               <slot></slot>
            </div>
         </div>
      `;
   }
}